import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AdLinkChatService from "@/services/AdLinkChatService";
import ImageGeneric from "@/assets/images/lg.png";
import {
  Check,
  LoaderCircle,
  Mic,
  Paperclip,
  Pencil,
  Search,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { useLayout } from "@/contexts/UseLayout";
import { AudioRecorder } from "@/components/MenuUtilidades/Chat/components/AudioRecorder";
import { AudioPlayer } from "@/components/MenuUtilidades/Chat/components/AudioPlayer";
import { MixedMessage } from "@/components/MenuUtilidades/Chat/components/MixedMessage";
import { FileUploadModal } from "@/components/Chat/shared/FileUploadModal";
import { DeleteConfirmModal } from "@/components/Chat/shared/DeleteConfirmModal";
import type { FileUploadItem } from "@/types/chat";
import { MAX_FILE_SIZE } from "@/types/chat";
import {
  OVERVIEW_QUERY_KEYS,
  QUERY_REFRESH_INTERVALS,
} from "@/constants/queryRefresh";

interface User {
  id: number;
  name: string;
  last_name?: string;
  email?: string;
  url_img?: { path?: string } | string | null;
  filePath?: string;
}

type MentionUser = User;

type ActiveMention = {
  start: number;
  query: string;
};

const MAX_MESSAGE_INPUT_HEIGHT = 128;

interface FileAttachment {
  id: number;
  name: string;
  path: string;
  size?: number;
  extname?: string;
}

interface Comment {
  id: number;
  content: string;
  user_id: number;
  addesk_id?: number;
  reunion_id?: number;
  pagamento_id?: number;
  createdAt: string;
  updatedAt: string;
  user: User;
  files: FileAttachment[];
}

interface AddeskChatProps {
  addeskId?: number;
  reunionId?: number;
  pagamentoId?: number;
  isReunion?: boolean;
  isPagamento?: boolean;
  mentionUsers?: MentionUser[];
}

const normalizeMentionText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getMentionUserName = (user?: MentionUser | null) =>
  `${user?.name ?? ""} ${user?.last_name ?? ""}`.trim();

const getMentionUserAvatar = (user?: MentionUser | null) => {
  const image = user?.url_img;
  if (typeof image === "string") return image;
  return image?.path ?? user?.filePath;
};

const getActiveMention = (
  value: string,
  caretPosition: number,
): ActiveMention | null => {
  const beforeCaret = value.slice(0, caretPosition);
  const triggerIndex = beforeCaret.lastIndexOf("@");

  if (triggerIndex === -1) return null;

  const charBeforeTrigger = beforeCaret[triggerIndex - 1];
  if (charBeforeTrigger && !/\s/.test(charBeforeTrigger)) return null;

  const query = beforeCaret.slice(triggerIndex + 1);
  if (query.includes("\n") || query.length > 60) return null;
  if (/[,.;:!?()[\]{}]/.test(query)) return null;

  return {
    start: triggerIndex,
    query,
  };
};

export function AddeskChat({
  addeskId,
  reunionId,
  pagamentoId,
  isReunion = false,
  isPagamento = false,
  mentionUsers = [],
}: AddeskChatProps) {
  const { userLogged } = useLayout();
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(
    null,
  );
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionProfile, setMentionProfile] = useState<{
    user: MentionUser;
    top: number;
    left: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mentionListRef = useRef<HTMLDivElement>(null);
  const mentionProfileRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const chatKind = isPagamento ? "pagamento" : isReunion ? "reunion" : "addesk";
  const itemId =
    chatKind === "pagamento"
      ? pagamentoId
      : chatKind === "reunion"
        ? reunionId
        : addeskId;
  const commentQueryKey =
    chatKind === "pagamento"
      ? ["pagamentoComments", pagamentoId]
      : chatKind === "reunion"
        ? ["reunionComments", reunionId]
        : ["addeskComments", addeskId];

  const fetchComments = () => {
    if (chatKind === "pagamento") {
      return AdLinkChatService.getPagamentoComments(pagamentoId!);
    }
    if (chatKind === "reunion") {
      return AdLinkChatService.getReunionComments(reunionId!);
    }
    return AdLinkChatService.getComments(addeskId!);
  };

  const sendChatComment = (
    content: string,
    files?: FileList | File[] | Blob | null,
    fileDisplayNames?: string[],
  ) => {
    if (chatKind === "pagamento") {
      return AdLinkChatService.sendPagamentoComment(
        pagamentoId!,
        content,
        files,
        fileDisplayNames,
      );
    }
    if (chatKind === "reunion") {
      return AdLinkChatService.sendReunionComment(
        reunionId!,
        content,
        files,
        fileDisplayNames,
      );
    }
    return AdLinkChatService.sendComment(
      addeskId!,
      content,
      files,
      fileDisplayNames,
    );
  };

  const roomParticipants = useMemo(() => {
    const participants = new Map<number, MentionUser>();

    mentionUsers.forEach((user) => {
      const userId = Number(user?.id);
      const userName = getMentionUserName(user);
      if (Number.isFinite(userId) && userName) {
        participants.set(userId, { ...user, id: userId });
      }
    });

    const loggedUserId = Number(userLogged?.data?.id);
    if (
      Number.isFinite(loggedUserId) &&
      getMentionUserName(userLogged?.data) &&
      !participants.has(loggedUserId)
    ) {
      participants.set(loggedUserId, {
        id: loggedUserId,
        name: userLogged?.data?.name,
        last_name: userLogged?.data?.last_name,
        email: userLogged?.data?.email,
        url_img: userLogged?.data?.url_img,
      });
    }

    return Array.from(participants.values());
  }, [mentionUsers, userLogged?.data]);

  const mentionableUsers = useMemo(() => {
    const loggedUserId = Number(userLogged?.data?.id);
    return roomParticipants.filter((user) => user.id !== loggedUserId);
  }, [roomParticipants, userLogged?.data?.id]);

  const mentionPatterns = useMemo(
    () =>
      roomParticipants
        .map((user) => ({
          user,
          mention: `@${getMentionUserName(user)}`,
        }))
        .filter((item) => item.mention.length > 1)
        .sort((a, b) => b.mention.length - a.mention.length),
    [roomParticipants],
  );

  const filteredMentionUsers = useMemo(() => {
    if (!activeMention) return [];

    const query = normalizeMentionText(activeMention.query);
    return mentionableUsers
      .filter((user) => {
        const name = normalizeMentionText(getMentionUserName(user));
        const email = normalizeMentionText(user.email ?? "");
        return !query || name.includes(query) || email.includes(query);
      })
      .slice(0, 6);
  }, [activeMention, mentionableUsers]);

  useEffect(() => {
    if (selectedMentionIndex >= filteredMentionUsers.length) {
      setSelectedMentionIndex(0);
    }
  }, [filteredMentionUsers.length, selectedMentionIndex]);

  // Invalida queries de overview para atualizar contadores de msgs não lidas
  const invalidateOverviewQueries = () => {
    OVERVIEW_QUERY_KEYS.forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
  };

  const {
    data: commentsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: commentQueryKey,
    queryFn: fetchComments,
    enabled: !!itemId,
    refetchInterval: QUERY_REFRESH_INTERVALS.activeComments,
    refetchIntervalInBackground: false,
  });

  const comments: Comment[] = useMemo(
    () => commentsData?.data ?? [],
    [commentsData?.data],
  );

  const getRequestError = (error: unknown, fallback: string) => {
    const requestError = error as {
      response?: { status?: number; data?: { message?: string } };
    };

    return {
      message: requestError.response?.data?.message ?? fallback,
      status: requestError.response?.status,
    };
  };

  const resetEditing = () => {
    setEditingCommentId(null);
    setEditContent("");
  };

  const startEditing = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };

  const handleUpdateComment = async (commentId: number) => {
    const content = editContent.trim();
    if (!content) {
      toast.warning("Digite o conteúdo do comentário");
      return;
    }

    setIsUpdatingComment(true);
    try {
      await AdLinkChatService.updateComment(commentId, content);
      toast.success("Comentário atualizado!");
      resetEditing();
      await queryClient.invalidateQueries({ queryKey: commentQueryKey });
    } catch (error) {
      const requestError = getRequestError(
        error,
        "Erro ao atualizar comentário",
      );
      toast.error(requestError.message);

      if (requestError.status === 404) {
        resetEditing();
        await queryClient.invalidateQueries({ queryKey: commentQueryKey });
      }
    } finally {
      setIsUpdatingComment(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await AdLinkChatService.deleteComment(commentToDelete.id);
      toast.success("Comentário excluído!");
      if (editingCommentId === commentToDelete.id) resetEditing();
      setCommentToDelete(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: commentQueryKey }),
        ...OVERVIEW_QUERY_KEYS.map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        ),
      ]);
    } catch (error) {
      const requestError = getRequestError(
        error,
        "Erro ao excluir comentário",
      );
      toast.error(requestError.message);

      if (requestError.status === 404) {
        setCommentToDelete(null);
        await queryClient.invalidateQueries({ queryKey: commentQueryKey });
        return;
      }

      throw error;
    }
  };

  useEffect(() => {
    setEditingCommentId(null);
    setEditContent("");
    setCommentToDelete(null);
  }, [chatKind, itemId]);

  useEffect(() => {
    if (!itemId || showUploadModal || showAudioRecorder) return;

    const timer = window.setTimeout(() => {
      messageInputRef.current?.focus({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [itemId, showUploadModal, showAudioRecorder]);

  useEffect(() => {
    const input = messageInputRef.current;
    if (!input) return;

    input.style.height = "auto";
    const nextHeight = Math.min(input.scrollHeight, MAX_MESSAGE_INPUT_HEIGHT);
    input.style.height = `${nextHeight}px`;
    input.style.overflowY =
      input.scrollHeight > MAX_MESSAGE_INPUT_HEIGHT ? "auto" : "hidden";
  }, [message]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activeMention &&
        mentionListRef.current &&
        !mentionListRef.current.contains(event.target as Node) &&
        !messageInputRef.current?.contains(event.target as Node)
      ) {
        setActiveMention(null);
      }

      if (
        mentionProfile &&
        mentionProfileRef.current &&
        !mentionProfileRef.current.contains(event.target as Node)
      ) {
        setMentionProfile(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeMention, mentionProfile]);

  // Auto-scroll para a última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.warning("Digite uma mensagem ou anexe um arquivo");
      return;
    }

    setIsSending(true);

    try {
      const contentToSend = message.trim();

      const response = await sendChatComment(contentToSend);

      if (response?.status === 201) {
        toast.success("Mensagem enviada!");
        setMessage("");

        // Atualiza a lista de comentários
        queryClient.invalidateQueries({
          queryKey: commentQueryKey,
        });

        // Atualiza contadores de msgs não lidas nas overviews
        invalidateOverviewQueries();
      } else {
        toast.error("Erro ao enviar mensagem");
      }
    } catch (err) {
      toast.error("Erro ao enviar mensagem");
      console.error(err);
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = event.target.value;
    setMessage(value);
    setActiveMention(getActiveMention(value, event.target.selectionStart ?? 0));
    setSelectedMentionIndex(0);
  };

  const insertMention = (user: MentionUser) => {
    const input = messageInputRef.current;
    const caretPosition = input?.selectionStart ?? message.length;
    const mentionRange =
      getActiveMention(message, caretPosition) ?? activeMention;

    if (!mentionRange) return;

    const mentionText = `@${getMentionUserName(user)} `;
    const nextMessage =
      message.slice(0, mentionRange.start) +
      mentionText +
      message.slice(caretPosition);
    const nextCaretPosition = mentionRange.start + mentionText.length;

    setMessage(nextMessage);
    setActiveMention(null);
    setSelectedMentionIndex(0);

    setTimeout(() => {
      messageInputRef.current?.focus();
      messageInputRef.current?.setSelectionRange(
        nextCaretPosition,
        nextCaretPosition,
      );
    }, 0);
  };

  const handleMentionProfileOpen = (
    user: MentionUser,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const width = 256;
    const height = 180;

    setMentionProfile({
      user,
      top: Math.max(
        8,
        Math.min(rect.bottom + 8, window.innerHeight - height - 8),
      ),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setPendingFiles(files);
      setShowUploadModal(true);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getUploadDisplayNames = (uploadItems: FileUploadItem[]) =>
    uploadItems.map((item) => {
      const extension = item.file.name.split(".").pop();
      const hasExtension =
        !!extension &&
        item.displayName.toLowerCase().endsWith(`.${extension.toLowerCase()}`);

      return hasExtension || !extension
        ? item.displayName
        : `${item.displayName}.${extension}`;
    });

  const handleUploadModalSend = async (
    uploadItems: FileUploadItem[],
    uploadMessage: string,
  ) => {
    const contentToSend =
      uploadMessage.trim() || message.trim() || "Arquivo(s) anexado(s)";
    const filesToSend = uploadItems.map((item) => item.file);
    const displayNames = getUploadDisplayNames(uploadItems);

    const response = await sendChatComment(
      contentToSend,
      filesToSend,
      displayNames,
    );

    if (response?.status === 201) {
      toast.success("Mensagem enviada!");
      setMessage("");
      setPendingFiles([]);
      setShowUploadModal(false);
      queryClient.invalidateQueries({
        queryKey: commentQueryKey,
      });
      invalidateOverviewQueries();
      return;
    }

    toast.error("Erro ao enviar mensagem");
    throw new Error("Erro ao enviar arquivo");
  };

  const handlePasteFiles = useCallback((event: ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];

    Array.from(items).forEach((item) => {
      if (item.kind !== "file") return;
      const file = item.getAsFile();
      if (!file) return;

      if (file.size > MAX_FILE_SIZE) {
        toast.error("Arquivo muito grande");
        return;
      }

      files.push(file);
    });

    if (files.length > 0) {
      event.preventDefault();
      setPendingFiles(files);
      setShowUploadModal(true);
    }
  }, []);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    container.addEventListener("paste", handlePasteFiles);
    return () => {
      container.removeEventListener("paste", handlePasteFiles);
    };
  }, [handlePasteFiles]);

  // Handlers para áudio
  const handleAudioReady = async (audioBlob: Blob) => {
    setIsSending(true);
    try {
      const formData = new FormData();
      formData.append("files", audioBlob, "audio.webm");

      if (chatKind === "pagamento") {
        formData.append("pagamento_id", pagamentoId!.toString());
      } else if (chatKind === "reunion") {
        formData.append("reunion_id", reunionId!.toString());
      } else {
        formData.append("addesk_id", addeskId!.toString());
      }

      // Se houver mensagem de texto também
      if (message.trim()) {
        formData.append("content", message.trim());
      } else {
        formData.append("content", "Áudio");
      }

      const response = await sendChatComment(
        message.trim() || "Áudio",
        audioBlob,
      );

      if (response?.status === 201) {
        toast.success("Áudio enviado!");
        setMessage("");
        setShowAudioRecorder(false);

        // Atualiza a lista de comentários
        queryClient.invalidateQueries({
          queryKey: commentQueryKey,
        });

        // Atualiza contadores de msgs não lidas nas overviews
        invalidateOverviewQueries();
      } else {
        toast.error("Erro ao enviar áudio");
      }
    } catch (error) {
      console.error("Erro ao enviar áudio:", error);
      toast.error("Erro ao enviar áudio");
    } finally {
      setIsSending(false);
    }
  };

  const handleCancelAudio = () => {
    setShowAudioRecorder(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const time = date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (isToday) {
      return `Hoje às ${time}`;
    } else if (isYesterday) {
      return `Ontem às ${time}`;
    } else {
      return `${date.toLocaleDateString("pt-BR")} às ${time}`;
    }
  };

  // Função para verificar se um arquivo é áudio
  const isAudioFile = (file: FileAttachment) => {
    const audioExtensions = [".webm", ".mp3", ".wav", ".ogg", ".m4a", ".aac"];
    const fileName = file.name.toLowerCase();
    return audioExtensions.some((ext) => fileName.endsWith(ext));
  };

  const getMentionMatchAt = (content: string, index: number) => {
    if (content[index] !== "@") return null;

    return (
      mentionPatterns.find(({ mention }) => {
        const candidate = content.slice(index, index + mention.length);
        if (candidate.toLowerCase() !== mention.toLowerCase()) return false;

        const nextCharacter = content[index + mention.length];
        return !nextCharacter || /[\s.,;:!?()[\]{}]/.test(nextCharacter);
      }) ?? null
    );
  };

  const renderTextWithMentions = (
    content: string,
    keyPrefix: string,
    isOwnMessage: boolean,
  ) => {
    const nodes = [];
    let cursor = 0;

    while (cursor < content.length) {
      const mentionMatch = getMentionMatchAt(content, cursor);

      if (mentionMatch) {
        nodes.push(
          <button
            key={`${keyPrefix}-mention-${cursor}`}
            type="button"
            onClick={(event) =>
              handleMentionProfileOpen(mentionMatch.user, event)
            }
            className={`inline-flex max-w-full align-baseline font-semibold underline-offset-2 hover:underline ${
              isOwnMessage ? "text-white" : "text-azul-400"
            }`}
            title="Ver perfil"
          >
            {content.slice(cursor, cursor + mentionMatch.mention.length)}
          </button>,
        );
        cursor += mentionMatch.mention.length;
        continue;
      }

      const nextMentionIndex = content.indexOf("@", cursor + 1);
      const endIndex =
        nextMentionIndex === -1 ? content.length : nextMentionIndex;
      nodes.push(
        <span
          key={`${keyPrefix}-text-${cursor}`}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {content.slice(cursor, endIndex)}
        </span>,
      );
      cursor = endIndex;
    }

    return nodes;
  };

  const renderTextContent = (content: string, isOwnMessage: boolean) => {
    if (!content) return null;

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = content.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all underline hover:no-underline"
            onClick={(event) => event.stopPropagation()}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {part}
          </a>
        );
      }

      return (
        <span key={index} style={{ whiteSpace: "pre-wrap" }}>
          {renderTextWithMentions(part, `part-${index}`, isOwnMessage)}
        </span>
      );
    });
  };

  // Função para renderizar conteúdo da mensagem baseado no tipo
  const renderMessageContent = (comment: Comment, isMyMessage: boolean) => {
    const files = comment.files || [];

    if (files.length === 0) {
      return <p>{renderTextContent(comment.content, isMyMessage)}</p>;
    }

    // Separar arquivos de áudio dos outros
    const audioFiles = files.filter((file) => isAudioFile(file));
    const otherFiles = files.filter((file) => !isAudioFile(file));

    // Se só tem áudio e nenhum texto relevante
    if (
      audioFiles.length > 0 &&
      otherFiles.length === 0 &&
      (!comment.content ||
        comment.content === "Áudio" ||
        comment.content === "Arquivo(s) anexado(s)")
    ) {
      return (
        <AudioPlayer
          files={audioFiles.map((f) => ({
            url: f.path,
            name: f.name,
            size: f.size ? parseInt(f.size.toString()) : undefined,
          }))}
          isOwnMessage={isMyMessage}
        />
      );
    }

    // Se tem mix de conteúdo (texto + arquivos ou áudio + outros arquivos)
    if (audioFiles.length > 0 || otherFiles.length > 0 || comment.content) {
      return (
        <MixedMessage
          content={comment.content}
          files={files.map((f) => ({
            url: f.path,
            name: f.name,
            size: f.size ? parseInt(f.size.toString()) : undefined,
            mimeType: f.extname ? `application/${f.extname}` : "",
          }))}
          isOwnMessage={isMyMessage}
        />
      );
    }

    // Fallback para texto simples
    return <p>{comment.content}</p>;
  };

  return (
    <div ref={chatContainerRef} className="flex flex-col h-full">
      {/* Header do Chat */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-medium">Chat</h2>
        <button
          type="button"
          className="p-1.5 hover:bg-cinza-100 rounded-lg transition-colors"
        >
          <Search size={18} className="text-cinza-500" />
        </button>
      </div>

      {/* Lista de Mensagens */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2 max-h-full">
        {isLoading && (
          <div className="text-center text-cinza-500 text-sm">
            Carregando mensagens...
          </div>
        )}

        {error && (
          <div className="text-center text-red-500 text-sm">
            Erro ao carregar mensagens
          </div>
        )}

        {!isLoading && !error && comments.length === 0 && (
          <div className="text-center text-cinza-500 text-sm">
            Nenhuma mensagem ainda. Seja o primeiro a comentar!
          </div>
        )}
        {comments.length > 0 &&
          comments?.map((comment) => {
            const userFullName = `${comment.user.name} ${comment.user.last_name}`;
            const userImage = getMentionUserAvatar(comment?.user) || ImageGeneric;
            const isMyMessage =
              Number(comment.user_id) === Number(userLogged?.data?.id);
            const isEditing = editingCommentId === comment.id;
            const wasEdited =
              new Date(comment.updatedAt).getTime() -
                new Date(comment.createdAt).getTime() >
              0;

            return (
              <div
                key={comment.id}
                className={`group flex gap-2 items-start ${
                  isMyMessage ? "flex-row-reverse" : ""
                }`}
              >
                {!isMyMessage && (
                  <img
                    src={userImage}
                    alt={userFullName}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                )}
                <div
                  className={`flex-1 min-w-0 ${
                    isMyMessage ? "flex flex-col items-end" : ""
                  }`}
                >
                  {!isMyMessage && (
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-sm font-medium truncate">
                        {userFullName}
                      </span>
                      <span className="text-[10px] text-cinza-500 flex-shrink-0">
                        {formatDate(comment.createdAt)}
                      </span>
                    </div>
                  )}
                  {isEditing ? (
                    <div className="w-full max-w-[80%] rounded-2xl rounded-tr-none bg-azul-600 p-3 text-white">
                      <textarea
                        autoFocus
                        value={editContent}
                        onChange={(event) => setEditContent(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Escape" && !isUpdatingComment) {
                            resetEditing();
                          }
                          if (
                            event.key === "Enter" &&
                            (event.ctrlKey || event.metaKey)
                          ) {
                            event.preventDefault();
                            void handleUpdateComment(comment.id);
                          }
                        }}
                        disabled={isUpdatingComment}
                        aria-label="Editar comentário"
                        className="min-h-20 w-full resize-y rounded-lg bg-white px-3 py-2 text-sm text-cinza-800 outline-none ring-2 ring-transparent focus:ring-azul-300 disabled:opacity-70"
                      />
                      <div className="mt-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={resetEditing}
                          disabled={isUpdatingComment}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-white/90 transition-colors hover:bg-white/10 disabled:opacity-50"
                        >
                          <X size={14} />
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleUpdateComment(comment.id)}
                          disabled={isUpdatingComment || !editContent.trim()}
                          className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-xs font-medium text-azul-400 transition-colors hover:bg-cinza-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isUpdatingComment ? (
                            <LoaderCircle size={14} className="animate-spin" />
                          ) : (
                            <Check size={14} />
                          )}
                          Salvar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`${
                        isMyMessage
                          ? "bg-azul-600 rounded-2xl rounded-tr-none"
                          : "bg-cinza-200 rounded-2xl rounded-tl-none"
                      } ${
                        isMyMessage ? "text-white" : "text-cinza-800"
                      } p-3 text-sm break-words max-w-[80%]`}
                    >
                      {renderMessageContent(comment, isMyMessage)}
                    </div>
                  )}
                  {isMyMessage && (
                    <div className="mt-1 flex max-w-[80%] flex-wrap items-center justify-end gap-x-3 gap-y-1">
                      <span className="text-[10px] text-cinza-500">
                        {formatDate(comment.createdAt)}
                        {wasEdited ? " · Editado" : ""}
                      </span>
                      {!isEditing && (
                        <div className="flex items-center gap-2 opacity-100 transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                          <button
                            type="button"
                            onClick={() => startEditing(comment)}
                            disabled={isUpdatingComment || !!commentToDelete}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-cinza-500 transition-colors hover:text-azul-400 disabled:opacity-50"
                            aria-label="Editar comentário"
                          >
                            <Pencil size={12} />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => setCommentToDelete(comment)}
                            disabled={isUpdatingComment || !!commentToDelete}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-cinza-500 transition-colors hover:text-red-500 disabled:opacity-50"
                            aria-label="Excluir comentário"
                          >
                            <Trash2 size={12} />
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        <div ref={messagesEndRef} />
      </div>

      {/* AudioRecorder */}
      {showAudioRecorder && (
        <div className="mb-3">
          <AudioRecorder
            onAudioReady={handleAudioReady}
            onCancel={handleCancelAudio}
            isSending={isSending}
          />
        </div>
      )}

      {/* Formulário de Envio */}
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          disabled={isSending}
        />

        <div className="flex-1 relative">
          {activeMention && filteredMentionUsers.length > 0 && (
            <div
              ref={mentionListRef}
              className="absolute bottom-full left-0 right-0 z-20 mb-2 max-h-72 overflow-y-auto rounded-lg border border-cinza-200 bg-white shadow-lg"
            >
              {filteredMentionUsers.map((user, index) => {
                const userName = getMentionUserName(user);
                const userAvatar = getMentionUserAvatar(user);
                const isSelected = index === selectedMentionIndex;

                return (
                  <button
                    key={user.id}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      insertMention(user);
                    }}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                      isSelected
                        ? "bg-[#E4EAFF] text-azul-400"
                        : "text-black hover:bg-cinza-100"
                    }`}
                  >
                    {userAvatar ? (
                      <img
                        src={userAvatar}
                        alt={userName}
                        className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-azul-400 text-xs font-semibold text-white">
                        {userName[0]?.toUpperCase()}
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {userName}
                      </span>
                      {user.email && (
                        <span className="block truncate text-xs text-cinza-500">
                          {user.email}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <textarea
            ref={messageInputRef}
            value={message}
            onChange={handleMessageChange}
            onSelect={(event) =>
              setActiveMention(
                getActiveMention(
                  event.currentTarget.value,
                  event.currentTarget.selectionStart ?? 0,
                ),
              )
            }
            onKeyDown={(event) => {
              if (activeMention && filteredMentionUsers.length > 0) {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setSelectedMentionIndex((prev) =>
                    Math.min(prev + 1, filteredMentionUsers.length - 1),
                  );
                  return;
                }
                if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setSelectedMentionIndex((prev) => Math.max(prev - 1, 0));
                  return;
                }
                if (event.key === "Enter" || event.key === "Tab") {
                  event.preventDefault();
                  insertMention(filteredMentionUsers[selectedMentionIndex]);
                  return;
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  setActiveMention(null);
                }
              }
            }}
            rows={1}
            placeholder="Digite sua mensagem..."
            disabled={isSending}
            className="addesk-chat-message-input block w-full min-h-[44px] max-h-32 resize-none overflow-y-hidden bg-cinza-100 rounded-3xl py-3 pl-4 pr-24 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-azul-600 disabled:opacity-50"
          />
          <div className="absolute right-3 bottom-3 flex gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending}
              className="text-cinza-500 hover:text-azul-600 transition-colors disabled:opacity-50"
              title="Anexar arquivo"
            >
              <Paperclip size={18} />
            </button>
            <button
              type="button"
              onClick={() => setShowAudioRecorder(!showAudioRecorder)}
              disabled={isSending}
              className={`transition-colors disabled:opacity-50 ${
                showAudioRecorder
                  ? "text-azul-600"
                  : "text-cinza-500 hover:text-azul-600"
              }`}
              title="Gravar áudio"
            >
              <Mic size={18} />
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSending || !message.trim()}
          className="bg-azul-600 text-white rounded-full p-3 hover:bg-azul-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send size={18} />
        </button>
      </form>

      <FileUploadModal
        isOpen={showUploadModal}
        initialFiles={pendingFiles}
        onClose={() => {
          setShowUploadModal(false);
          setPendingFiles([]);
        }}
        onSend={handleUploadModalSend}
      />

      <DeleteConfirmModal
        isOpen={!!commentToDelete}
        onClose={() => setCommentToDelete(null)}
        onConfirm={handleDeleteComment}
      />

      {mentionProfile && (
        <div
          ref={mentionProfileRef}
          style={{
            position: "fixed",
            top: mentionProfile.top,
            left: mentionProfile.left,
          }}
          className="z-[80] w-64 rounded-lg border border-cinza-200 bg-white p-4 text-black shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {getMentionUserAvatar(mentionProfile.user) ? (
                <img
                  src={getMentionUserAvatar(mentionProfile.user)}
                  alt={getMentionUserName(mentionProfile.user)}
                  className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-azul-400 text-sm font-semibold text-white">
                  {getMentionUserName(mentionProfile.user)[0]?.toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">
                  {getMentionUserName(mentionProfile.user)}
                </p>
                {mentionProfile.user.email && (
                  <p className="truncate text-xs text-cinza-500">
                    {mentionProfile.user.email}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMentionProfile(null)}
              className="rounded p-1 text-cinza-500 transition-colors hover:bg-cinza-100 hover:text-black"
              title="Fechar"
            >
              <X size={14} />
            </button>
          </div>
          <div className="mt-3 rounded-md bg-cinza-100 px-3 py-2 text-xs text-cinza-500">
            Participante deste chat
          </div>
        </div>
      )}
    </div>
  );
}
